import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';

import Auth from '@aws-amplify/auth';
import config from 'config';
import { useFormik } from 'formik';
import { Button, ErrorMessage, LinkButton, UiInput } from 'ui-kit';
import * as yup from 'yup';

import { useLazyQuery } from '@apollo/client';

import { AuthContext, AuthContextAction, AuthUser } from '../../context/AuthContext';
import { useFeatureFlag } from '../../context/FeatureFlagContext';
import { DISCOVER_SSO_PROVIDER } from '../../query/sso';
import { useTour } from '../../tours/useTour';
import { storeSsoLogout } from '../../utils/authUtils';
import { initiateSsoRedirect } from '../../utils/ssoLogin';
import { beginSsoRetrySession, clearSsoRetrySession } from '../../utils/ssoRetry';

type Credentials = {
  email: string;
  password: string;
};

type LoginStep = 'email' | 'password';

const initialValues: Credentials = {
  email: '',
  password: '',
};

const emailValidationSchema = yup.object().shape({
  email: yup.string().email('Invalid email address').required('Email is required'),
});

const passwordValidationSchema = yup.object().shape({
  password: yup.string().required('Password is required'),
});

type SignInFormProps = {
  setIsNewPasswordRequired: (user: AuthUser, credentials: Credentials) => void;
  setCredentials: Dispatch<SetStateAction<Credentials>>;
  onFlip: () => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
};

export const SignInForm = ({
  setIsNewPasswordRequired,
  setCredentials,
  onFlip,
  setIsLoading,
  isLoading = false,
}: SignInFormProps): JSX.Element => {
  const { state, dispatch } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState<LoginStep>('email');
  const currentStepRef = useRef<LoginStep>(currentStep);
  const [discoverSSO, { loading: ssoLoading }] = useLazyQuery(DISCOVER_SSO_PROVIDER, {
    errorPolicy: 'ignore', // Don't crash if auth fails - this query runs before login
  });
  const { startTour } = useTour();

  // Keep ref in sync with state
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  // Start tour when step changes
  useEffect(() => {
    if (currentStep === 'email') {
      startTour('auth.signIn.email');
    } else if (currentStep === 'password') {
      startTour('auth.signIn.password');
    }
  }, [currentStep, startTour]);

  const formik = useFormik({
    initialValues,
    validate: async (values) => {
      const schema =
        currentStepRef.current === 'email' ? emailValidationSchema : passwordValidationSchema;
      try {
        await schema.validate(values, { abortEarly: false });
        return {};
      } catch (err: any) {
        const errors: { [key: string]: string } = {};
        if (err.inner) {
          err.inner.forEach((error: any) => {
            if (error.path) {
              errors[error.path] = error.message;
            }
          });
        }
        return errors;
      }
    },
    onSubmit: async (values) => {
      if (currentStepRef.current === 'email') {
        setIsLoading(true);
        // Clear any error surfaced by a previous attempt before starting.
        dispatch({ type: AuthContextAction.AUTH_ERROR, payload: { error: null } });

        try {
          const { data } = await discoverSSO({
            variables: { email: values.email.toLowerCase() },
          });

          const ssoResult = data?.discoverSSOProvider;

          if (ssoResult?.hasSso && ssoResult?.loginMethod === 'SSO') {
            const redirectParams = {
              authorizeEndpoint: ssoResult.cognitoAuthorizationEndpoint || '',
              clientId: ssoResult.cognitoClientId || '',
              identityProvider: ssoResult.identityProvider || undefined,
              scope: ssoResult.scope || undefined,
            };
            try {
              // Persist what logout and the timeout auto-retry (ENG-2910) need
              // before we navigate away. A fresh manual login starts a new retry
              // session (attempt count reset to zero).
              if (ssoResult.cognitoAuthorizationEndpoint) {
                storeSsoLogout(
                  ssoResult.cognitoAuthorizationEndpoint,
                  ssoResult.cognitoClientId || '',
                );
                beginSsoRetrySession(redirectParams);
              }

              // The OAuth redirect needs Web Crypto + session storage; this throws
              // with a named reason if this browser blocks them.
              await initiateSsoRedirect(redirectParams, config.website.URL);
              return;
            } catch (err) {
              // SSO is configured for this account but the redirect can't be
              // started in this browser. Do not fall back to password: an SSO
              // user has no password here, so a silent fallback would dead-end
              // them on the form with no explanation. Surface the reason and
              // drop the retry session so it can't trigger a stray retry.
              clearSsoRetrySession();
              console.error('Failed to start SSO sign-in', err);
              setIsLoading(false);
              const reason = err instanceof Error ? err.message : 'unexpected error';
              dispatch({
                type: AuthContextAction.AUTH_ERROR,
                payload: {
                  error: `We couldn't start single sign-on in this browser (${reason}). Try an up-to-date Chrome, Edge, or Safari window, or contact support@orsnn.com.`,
                },
              });
              return;
            }
          }
        } catch (err) {
          // Discovery itself failed (network/GraphQL). We can't tell whether SSO
          // applies, so fall through to password login — but log it rather than
          // swallowing it silently as before.
          console.error('SSO discovery failed; falling back to password login', err);
        }

        // No SSO for this email (or discovery failed) — continue with password.
        setIsLoading(false);
        setCurrentStep('password');
        return;
      }

      // Handle password submission
      setIsLoading(true);
      try {
        const user: AuthUser = await Auth.signIn(values.email.toLowerCase(), values.password);
        if (user.signInUserSession) {
          setIsLoading(false);
          dispatch({
            type: AuthContextAction.SET_SIGNED_IN_USER,
            payload: user,
          });
        } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setIsLoading(false);
          const creds = { email: values.email, password: values.password };
          setCredentials(creds);
          setIsNewPasswordRequired(user, creds);
        }
      } catch (err: any) {
        setIsLoading(false);
        dispatch({
          type: AuthContextAction.AUTH_ERROR,
          payload: { error: err.code },
        });
      }
    },
  });

  // Re-validate when step changes
  useEffect(() => {
    formik.setFieldTouched('password', false);
    formik.validateForm();
  }, [currentStep]);

  const handleBackToEmail = () => {
    // Update ref synchronously before validation to ensure correct schema is used
    currentStepRef.current = 'email';
    setCurrentStep('email');
    formik.setFieldValue('password', '');
    formik.setFieldTouched('password', false);
    // Re-validate against email schema after going back
    formik.validateForm();
    startTour('auth.signIn.email');
    dispatch({
      type: AuthContextAction.AUTH_ERROR,
      payload: { error: null },
    });
  };

  return (
    <>
      <div className="mb-3 text-left font-heebo text-white">
        <h2 className="text-[24px] font-bold">Welcome Back</h2>
        <p className="mt-2 text-[16px]">
          {currentStep === 'email'
            ? 'Sign in to access your trading dashboard'
            : `Enter your password for ${formik.values.email}`}
        </p>
      </div>

      <form
        onSubmit={formik.handleSubmit}
        className="flex flex-col gap-3"
        data-testid="sign-in-form"
      >
        {currentStep === 'email' ? (
          <>
            <div>
              <UiInput
                name="email"
                type="text"
                size="extra-large"
                variant="dark"
                label="Email"
                placeholder="Institution Email Address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                className="font-heebo"
                data-testid="email-input"
                data-tour="email-input"
                autoFocus
              />
              <ErrorMessage className="mt-1 font-heebo" data-testid="email-error">
                {formik.touched.email && formik.errors.email}
              </ErrorMessage>
            </div>

            {typeof state.authError === 'string' && state.authError && (
              <ErrorMessage className="mt-1 font-heebo" data-testid="sso-error">
                {state.authError}
              </ErrorMessage>
            )}

            <Button
              label="Continue"
              buttonType="submit"
              type="primary_light"
              disabled={!formik.isValid || isLoading || ssoLoading}
              className="bg-gradient-to-r from-violet-500 to-violet-600"
              data-testid="continue-button"
            >
              Continue
            </Button>
          </>
        ) : (
          <>
            <div>
              <UiInput
                name="email"
                type="text"
                size="extra-large"
                variant="dark"
                label="Email"
                value={formik.values.email}
                disabled
                className="font-heebo opacity-60"
                data-testid="email-display"
              />
            </div>

            <div>
              <UiInput
                name="password"
                type="password"
                size="extra-large"
                variant="dark"
                label="Password"
                placeholder="Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                className="font-heebo"
                data-testid="password-input"
                data-tour="password-input"
                autoFocus
              />
              <ErrorMessage className="mt-1 font-heebo" data-testid="password-error">
                {formik.touched.password && formik.errors.password}
              </ErrorMessage>

              {state.authError && (
                <ErrorMessage className="mt-2">
                  Login failed. Please check your credentials or click Forgot Password
                </ErrorMessage>
              )}

              <div className="mt-2 text-left">
                <LinkButton
                  text="Forgot password?"
                  onClick={onFlip}
                  className="font-heebo text-sm"
                  underline={false}
                  data-testid="forgot-password-link"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                label="Back"
                buttonType="button"
                type="secondary"
                onClick={handleBackToEmail}
                disabled={isLoading}
                className="flex-1"
                data-testid="back-button"
              >
                Back
              </Button>
              <Button
                label="Sign In"
                buttonType="submit"
                type="primary_light"
                disabled={!formik.isValid || isLoading}
                className="flex-1 bg-gradient-to-r from-violet-500 to-violet-600"
                data-testid="sign-in-button"
              >
                Sign In
              </Button>
            </div>
          </>
        )}
      </form>
    </>
  );
};
